import React from 'react';
import { noop } from 'lodash';
import { InternalCalendar, SchedulerDay } from './';
import { Tooltip } from '..';
import { Utils } from '../../Utils';

export interface IScheduleProps {
    selectedDate?: Date;
    colorPalette?: string[];
    eventsOnCalendar: ISchedulerDate[];
    enableMonthSelection?: boolean;
    onShowDetails(events: ISchedulerDate): void;
    onMonthChange?(): void;
}

export interface ISchedulerDate {
    color?: string;
    startDate: Date;
    endDate: Date;
    eventID: string;
}

interface IAssignedColors {
    id: string;
    color: string;
}

export const Schedule: React.FunctionComponent<IScheduleProps> = (props): React.ReactElement => {
    const colorPalette: string[] = props.colorPalette ? props.colorPalette : [
        `#00AE4E`, `#88BD39`, `#FFC000`, `#C29200`, `#E52030`, `#A51C30`,
        `#4DB6C9`, `#6076BA`, `#BA6897`, `#AD62E5`, `#4DB6C9`, `#6076BA`,
        `#8F6DAD`, `#BA6897`, `#E7DBE7`
    ],
        selectedDate: Date = props.selectedDate ? new Date(props.selectedDate) : new Date(),
        _str2date = (strDate: string | Date): Date => {
            if (typeof strDate === `string`) {
                const d1 = strDate.split(` `);
                const date = d1[0].split(`/`);
        
                const dd = Number(date[0]),
                    mm = Number(date[1]),
                    yy = Number(date[2]);
        
                return new Date(yy, mm - 1, dd, 0, 0, 0);
            }
            else {
                return strDate;
            }
        };

    const assignedColours: IAssignedColors[] = props.eventsOnCalendar.map((e, index): IAssignedColors => {
        if (e.color) {
            return {
                id: e.eventID,
                color: e.color
            }
        }
        else {
            return {
                id: e.eventID,
                color: colorPalette[index]
            }
        }
    });

    const renderDayInPicker = (date: Date, dateSelected: Date, dayInCurrentMonth: boolean, dayComponent: JSX.Element): JSX.Element => {
        const dateParsed = new Date(date),
            affectedDates: ISchedulerDate[] = props.eventsOnCalendar.filter(a => {
            const startDateParsed = _str2date(a.startDate),
                endDateParsed = _str2date(a.endDate);
           
            if (startDateParsed.getTime() === endDateParsed.getTime()) {
                return startDateParsed.getTime() === dateParsed.getTime();
            }
            else {
                return startDateParsed.getTime() <= dateParsed.getTime() && endDateParsed.getTime() >= dateParsed.getTime()
            }
        });

        if (affectedDates.length > 0 && dayInCurrentMonth) {
            return (
                <div>
                    <Tooltip label={
                            <React.Fragment>
                                {affectedDates.map(d => (
                                    <div onClick={() => props.onShowDetails(d)}
                                        style={{
                                            textDecoration: `underline`,
                                            cursor: `pointer`,
                                            marginTop: `3px`
                                        }}
                                    >
                                        {d.eventID}
                                    </div>
                                ))}
                            </React.Fragment>
                        }
                        interactive={true}
                        preferredPlacement="bottom"
                    >
                        <span>
                            {createSVG(affectedDates)}
                            <SchedulerDay children={dayComponent.props.children} />
                        </span>
                    </Tooltip>
                </div>
            );
        }
        else if (!dayInCurrentMonth) {
            return <SchedulerDay hidden={true} children={dayComponent.props.children} />;
        }
        else {
            return <SchedulerDay children={dayComponent.props.children} />;
        }
    };

    const createSVG = (dates: ISchedulerDate[]): JSX.Element => {
        const colours = assignedColours.filter(c => dates.map(d => d.eventID).indexOf(c.id) !== -1).map(c => c.color);

        if (colours.length === 1) {
            return (
                <svg style={{
                    height: `30px`,
                    margin: `4px 0 0 5px`,
                    position: `absolute`,
                    width: `30px`
                }}
                    data-name={`Layer 1`}
                    id={`Layer_1`}
                    viewBox={`0 0 25 25`}
                    xmlns={`http://www.w3.org/2000/svg`}
                >
                    <circle cx={`12.5`}
                        cy={`12.5`}
                        fill={colours[0]}
                        r={`12.5`}
                    />
                </svg>
            );
        }
        else {
            const radius = 14,
                stepAngle: number = 2 * Math.PI / colours.length;

            let lastX = 0,
                lastY: number = -radius;

            const defs: JSX.Element[] = [],
                guid: string = Utils.newGuid(),
                paths: JSX.Element[] = [];

            for (let k = 1; k <= colours.length; k++) {
                const angle: number = k * stepAngle,
                    x: number = radius * Math.sin(angle), // Calculate this arc end point.
                    y: number = -radius * Math.cos(angle);

                paths.push(
                    <path d={`M ${lastX.toFixed(3)} ${lastY.toFixed(3)} A ${radius} ${radius} 0 0 1 ${x.toFixed(3)} ${y.toFixed(3)}`}
                        key={`${guid}wheelseg${k}-path`}
                        stroke={`url(#${guid}wheelseg${k})`}
                    />
                );

                defs.push(
                    <linearGradient gradientUnits={`userSpaceOnUse`}
                        id={`${guid}wheelseg${k}`}
                        key={`${guid}wheelseg${k}-gradient`}
                        x1={lastX.toFixed(3)}
                        x2={x.toFixed(3)}
                        y1={lastY.toFixed(3)}
                        y2={y.toFixed(3)}
                    >
                        <stop offset={`0%`}
                            stopColor={colours[k - 1]}
                        />
                        <stop offset={`100%`}
                            stopColor={colours[k - 1]}
                        />
                    </linearGradient>
                );

                lastX = x;
                lastY = y;
            }

            return (
                <svg style={{
                    height: `30px`,
                    margin: `4px 0 0 5px`,
                    position: `absolute`,
                    width: `30px`
                }}
                    data-name={`Layer 1`}
                    id={`Layer_1`}
                    viewBox={`0 0 34 34`}
                    xmlns={`http://www.w3.org/2000/svg`}
                >
                    <defs id={`ticketpaths1583280000000`}>
                        {defs}
                    </defs>
                    <g fill={`none`}
                        id={`ticketpaths1583280000000`}
                        strokeWidth={`5px`}
                        transform={`translate(17, 17)`}
                    >
                        {paths}
                    </g>
                </svg>
            );
        }
    };

    return (
        <InternalCalendar
            date={new Date(selectedDate)} 
            onChange={noop}
            renderDay={renderDayInPicker}
            minDate={new Date(new Date(selectedDate).getFullYear(), new Date(selectedDate).getMonth() + 1, 0)}
            maxDate={new Date(new Date(selectedDate).getFullYear(), new Date(selectedDate).getMonth() + 1, 0)}
        />
    );
};